// Espejo ESM de aws_lambdas/_shared_pedidos/orderCancelCutoff.js (24/9/2026) para el simulador de ventas (card y Flow simulado):
// la VISTA PREVIA tiene que decir lo mismo que recibe el cliente. El cuerpo entre las marcas es
// IDÉNTICO al canónico (lo fija aws_lambdas/_shared_pedidos/fulfillmentCopies.test.js).

// ── orderCancelCutoff: cuerpo compartido (inicio) ──

// Tope razonable (1 día). La UI ofrece 15, 30, 45, 60, 90 y 120.
const MAX_CUTOFF_MINUTES = 1440;
const CUTOFF_OPTIONS = [0, 15, 30, 45, 60, 90, 120];

/** Minutos válidos (entero 0..1440) o null si el dato no vale. */
function normalizeCutoffMinutes(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  if (!Number.isInteger(n) || n < 0 || n > MAX_CUTOFF_MINUTES) return null;
  return n;
}

/**
 * E.164 ("+34654987217") o "" si no lo es. MISMO regex que citas (`/^\+[1-9]\d{7,14}$/`): un dato
 * roto nunca se cuela en el mensaje que lee el cliente.
 */
function normalizeCancelPhone(phone) {
  if (typeof phone !== "string") return "";
  const clean = phone.replace(/[\s.()-]/g, "");
  return /^\+[1-9]\d{7,14}$/.test(clean) ? clean : "";
}

/**
 * Teléfono del negocio (`settings.business_phone`) para el mensaje de corte. Se guarda a mano en
 * la config de pedidos y muchas veces sin prefijo («960 00 00 00»): un nacional español (9 dígitos
 * que empiezan por 6-9, con o sin espacios/guiones/puntos) pasa a +34 antes de validar E.164. Lo
 * demás, tal cual por normalizeCancelPhone (si no es E.164, "": el mensaje va sin número).
 */
function normalizeBusinessPhone(phone) {
  if (typeof phone !== "string") return "";
  const clean = phone.replace(/[\s.()-]/g, "");
  if (/^[6-9]\d{8}$/.test(clean)) return `+34${clean}`;
  return normalizeCancelPhone(clean);
}

/**
 * Teléfono LEGIBLE para el mensaje que lee el cliente (24/9/2026, David): «+34657524749» pegado
 * no se lee. Español (+34 y 9 dígitos) → «+34 657 52 47 49» (3-2-2-2, como se dicta un número en
 * España; vale igual para fijos: «+34 960 60 00 24»). Otros países, tal cual en E.164: cada uno
 * agrupa a su manera y un agrupado inventado es peor que ninguno. No E.164 → "".
 */
function formatPhoneForMessage(phone) {
  const tel = normalizeCancelPhone(phone);
  const es = /^\+34(\d{3})(\d{2})(\d{2})(\d{2})$/.exec(tel);
  return es ? `+34 ${es[1]} ${es[2]} ${es[3]} ${es[4]}` : tel;
}

/**
 * ¿Está el pedido dentro del corte? cutoff > 0 y faltan ≤ cutoff minutos para la recogida (o ya
 * pasó). Hora de recogida inválida → false (no se bloquea por un dato corrupto).
 */
function isWithinOrderCancelCutoff({ pickupMs, nowMs, cutoffMinutes }) {
  const m = Number(cutoffMinutes);
  if (!Number.isFinite(m) || m <= 0) return false;
  if (!Number.isFinite(pickupMs)) return false;
  return pickupMs - nowMs <= m * 60000;
}

/** «30 min», «1 hora», «1 h y 30 min», «2 horas» (es) / «30 min», «1 hora», «1 h i 30 min», «2 hores» (va). */
function formatCutoff(minutes, lang) {
  const m = Number(minutes) || 0;
  const va = String(lang || "").toLowerCase() === "va";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (rest === 0) return h === 1 ? (va ? "1 hora" : "1 hora") : `${h} ${va ? "hores" : "horas"}`;
  return `${h} h ${va ? "i" : "y"} ${rest} min`;
}

/**
 * Texto que recibe el cliente. Texto libre por c_sendMessage en la ventana de 24 h que abre él
 * mismo al pulsar el botón: SIN plantilla de Meta. Idiomas: es (por defecto) y va.
 */
function cancelCutoffMessage(minutes, phone, lang) {
  const when = formatCutoff(minutes, lang);
  const tel = formatPhoneForMessage(phone);
  if (String(lang || "").toLowerCase() === "va") {
    return tel
      ? `La teua comanda és en menys de ${when} i ja l'estem preparant: per a cancel·lar-la, telefona'ns al ${tel}.`
      : `La teua comanda és en menys de ${when} i ja l'estem preparant, així que ja no es pot cancel·lar des d'ací. Si necessites alguna cosa, escriu-nos per este xat.`;
  }
  return tel
    ? `Tu pedido es en menos de ${when} y ya lo estamos preparando: para cancelarlo, llámanos al ${tel}.`
    : `Tu pedido es en menos de ${when} y ya lo estamos preparando, así que ya no se puede cancelar desde aquí. Si necesitas algo, escríbenos por este chat.`;
}

/**
 * «Teléfono que verán tus clientes» de pedidos (24/9/2026, David): UNA sola resolución para el
 * corte de cancelación, «ahora mismo no aceptamos pedidos», «hoy no hemos publicado la carta» y
 * las difusiones de la carta. Desde `settings` (plano):
 *   1. `pedidos.cancel_contact_phone` (el que el dueño pone en la card «Cancelar pedidos») → "own";
 *   2. `settings.business_phone` (el antiguo «Teléfono para pedidos»; LEGADO: solo existe mientras
 *      la empresa no guarde desde la card, que lo borra) → "business_phone";
 *   3. nada → { phone: "", source: "" }: el mensaje va en su variante SIN número.
 * NUNCA el móvil personal del dueño (`c_empresas.phone_e164`): hasta hoy «no aceptamos pedidos»
 * lo daba sin que él lo eligiera. Solo sale si el dueño lo escribe aquí (la card se lo ofrece
 * con un aviso de que es su móvil personal).
 */
function resolveCustomerContactPhone(settings) {
  const p = (settings && settings.pedidos) || {};
  const own = normalizeCancelPhone(p.cancel_contact_phone);
  if (own) return { phone: own, source: "own" };
  const biz = normalizeBusinessPhone(settings && settings.business_phone);
  if (biz) return { phone: biz, source: "business_phone" };
  return { phone: "", source: "" };
}

/** Config efectiva del corte desde `settings` (plano). Teléfono: resolveCustomerContactPhone. */
function resolveCancelCutoffConfig(settings) {
  const p = (settings && settings.pedidos) || {};
  const minutes = normalizeCutoffMinutes(p.cancel_cutoff_minutes);
  return {
    minutes: minutes == null ? 0 : minutes,
    phone: resolveCustomerContactPhone(settings).phone,
    timeZone: (p.fulfillment && p.fulfillment.timezone) || "Europe/Madrid",
  };
}

/**
 * Teléfono del GET /pedidos/config (el navegador). El lambda ya lo resuelve en `customer_phone`;
 * con un lambda anterior (sin ese campo) se resuelve aquí con la MISMA función.
 */
function customerPhoneFromPedidosConfig(cfg) {
  const c = cfg || {};
  if (typeof c.customer_phone === "string") return normalizeCancelPhone(c.customer_phone);
  return resolveCustomerContactPhone({
    business_phone: c.business_phone,
    pedidos: { cancel_contact_phone: c.cancel_contact_phone },
  }).phone;
}

/**
 * Teléfono listo para ir dentro de un texto al cliente, o "" (24/9/2026, David: «que no dé error:
 * que se rellene con algún texto»). Nunca "undefined", "null" ni "-": sin teléfono válido, "" y el
 * mensaje va en su variante sin número.
 */
function contactPhoneText(phone) {
  return formatPhoneForMessage(normalizeBusinessPhone(phone == null ? "" : String(phone)));
}

/**
 * Formulario de WhatsApp (c_cartaDiaFlow): el cliente elige «A domicilio» y ahora mismo no se
 * puede. Sale en un TextBody del Flow (límite de Meta 4096): el número largo cabe de sobra.
 */
function deliveryUnavailableMessage(phone) {
  const tel = contactPhoneText(phone);
  return tel
    ? `Ahora mismo no podemos tomar pedidos a domicilio por WhatsApp. Llámanos al ${tel}, por favor.`
    : "Ahora mismo no podemos tomar pedidos a domicilio por WhatsApp. Escríbenos por este chat y te ayudamos.";
}

/**
 * Textos de error del Formulario de WhatsApp (c_cartaDiaFlow y c_menuDiaFlow, pantalla final).
 * Antes decían «llámanos directamente» sin decir a qué número. Casos:
 *   - "items_missing": no se encontraron los platos elegidos.
 *   - "closed": ya no se aceptan pedidos (va en la segunda línea, tras «no se ha registrado»).
 *   - "failed" (o cualquier otro): no se pudo guardar el pedido.
 * TextBody (límite 4096): cabe siempre.
 */
function flowOrderErrorMessage(kind, phone) {
  const tel = contactPhoneText(phone);
  if (kind === "items_missing") {
    return tel
      ? `No se encontraron los platos seleccionados. Llámanos al ${tel} y te tomamos el pedido.`
      : "No se encontraron los platos seleccionados. Escríbenos por este chat y te tomamos el pedido.";
  }
  if (kind === "closed") {
    return tel
      ? `Vuelve a intentarlo cuando la carta esté disponible o llámanos al ${tel}.`
      : "Vuelve a intentarlo cuando la carta esté disponible o escríbenos por este chat.";
  }
  return tel
    ? `No se pudo procesar el pedido. Por favor, llámanos al ${tel} y te lo tomamos.`
    : "No se pudo procesar el pedido. Por favor, escríbenos por este chat y te lo tomamos.";
}

/**
 * {{telefono}} de las plantillas de difusión de la carta (APROBADAS en Meta: el texto de alrededor
 * no se puede cambiar). Meta rechaza un parámetro vacío (131008) y antes iba «-» («llamar al -»).
 * Sin teléfono, un relleno que se lee bien DENTRO de cada frase aprobada:
 *   cartadiaflow  «También puedes realizar el pedido llamando al {{telefono}}.»
 *   platodiaflow  «También puedes llamar al {{telefono}}»
 *   carta_tlf, carta_tlf_v2, plato_tlf  «Teléfono: {{telefono}}»
 * Plantilla desconocida → el relleno de «Teléfono:», que se lee en cualquier frase.
 */
const CARTA_TEMPLATE_PHONE_FILLERS = {
  cartadiaflow: "local o escribiéndonos por este chat",
  platodiaflow: "local o escribirnos por este chat",
  carta_tlf: "responde a este mensaje y te lo pasamos",
  carta_tlf_v2: "responde a este mensaje y te lo pasamos",
  plato_tlf: "responde a este mensaje y te lo pasamos",
};
function cartaTemplatePhoneParam(templateName, phone) {
  const tel = contactPhoneText(phone);
  if (tel) return tel;
  const key = String(templateName || "").toLowerCase();
  return CARTA_TEMPLATE_PHONE_FILLERS[key] || CARTA_TEMPLATE_PHONE_FILLERS.carta_tlf;
}

// ── orderCancelCutoff: cuerpo compartido (fin) ──

export {
  MAX_CUTOFF_MINUTES,
  CUTOFF_OPTIONS,
  normalizeCutoffMinutes,
  normalizeCancelPhone,
  normalizeBusinessPhone,
  formatPhoneForMessage,
  isWithinOrderCancelCutoff,
  formatCutoff,
  cancelCutoffMessage,
  resolveCustomerContactPhone,
  resolveCancelCutoffConfig,
  customerPhoneFromPedidosConfig,
  contactPhoneText,
  deliveryUnavailableMessage,
  flowOrderErrorMessage,
  CARTA_TEMPLATE_PHONE_FILLERS,
  cartaTemplatePhoneParam,
};
